import { Time } from '@internationalized/date';
import {
	ActionButton,
	Picker,
	PickerItem,
	Text,
	TextArea,
	TextField,
	TimeField,
} from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import Close from '@react-spectrum/s2/icons/Close';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useEchoBaseline } from '@/hooks/useEchoBaseline';
import { cn } from '@/utils/cn';

import type {
	CustomMonitor,
	MonitorCadence,
	MonitorSource,
} from '../../../../../bridge/types/artifact.ts';

import {
	CADENCE_PRESETS,
	MAX_MONITOR_SOURCES,
	SOURCE_TOOL_HINTS,
	SOURCE_TOOL_LABELS,
	checkMonitorTarget,
	cadenceUsesTime,
	monitorSummarySentence,
	monitorTrim,
	twiceDailyTimesLabel,
} from './monitorSummary.ts';

const MAX_SOURCES = MAX_MONITOR_SOURCES;
const DEFAULT_CADENCE: MonitorCadence = { preset: 'daily', at: '09:00' };
const INSTRUCTION_LIMIT = 1000;
// The character counter is noise until the user is near the cap, so it only
// appears once the instruction passes ~60% of the limit.
const COUNTER_THRESHOLD = Math.floor(INSTRUCTION_LIMIT * 0.6);

// Handlers the surrounding panel supplies. Text/source/output edits persist
// immediately via onContentChange (the do_edit_artifact idiom); cadence edits
// buffer locally and flush as one edit at save/activate time to avoid
// per-keystroke heartbeat churn on the server. The flush sends only the cadence
// fields someone actually changed, so opening and closing a panel writes nothing.
export interface MonitorBodyHandlers {
	onContentChange: (updates: Record<string, unknown>) => void;
	// Enables Save / marks the panel dirty without persisting — used by the
	// buffered cadence controls.
	onDirty?: () => void;
	// Hands the panel a flush function it invokes before do_save_artifact /
	// activation, so buffered cadence is written exactly once.
	registerFlush?: (flush: () => void) => void;
	// True while a source box holds text the sensing tool cannot use. That text is
	// buffered here and deliberately NOT written to the artifact, so the panel
	// cannot see it in `artifact.content` — without this signal Save/Activate
	// would re-enable against the last good stored value while the box on screen
	// still shows a rejected target (ADO-1890).
	onRejectedTargetChange?: (hasRejectedTarget: boolean) => void;
}

interface MonitorConfigBodyProps {
	artifact: CustomMonitor;
	handlers: MonitorBodyHandlers;
	isReadOnly?: boolean;
	// The active job panel shows an operational meta row under the header; the
	// draft panel omits it.
	showMetaRow?: boolean;
	// True when the panel's owner knows the last save attempt did not reach the
	// document. The echo-baseline records assume their own write landed —
	// without this signal, a retry of the exact same edit reads as already
	// sent and nothing goes out a second time (ADO-2047).
	saveFailed?: boolean;
}

// Each group of controls is its own card. Four bare uppercase micro-labels over a
// flat stack gave every group the same weight as the fields under it, so nothing
// read as a boundary and the form was a single undifferentiated column. A hairline
// card states where a group starts and ends without adding a fill: `glass-card-sm`
// carries the panel's own glass on top of the panel's own glass, so the 1px border
// and the 1rem radius do the delineating. A tint here would invert figure/ground —
// the grey is what makes glass read as raised, so tinting these would push them
// behind the surface they sit on.
const SectionCard = ({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) => (
	<section className="glass-card-sm flex flex-col gap-3 p-4">
		{/* Sentence case at body weight, not micro-caps: this is the card's title, and
		    it has to out-rank the S2 field labels inside the card rather than tie with
		    them. */}
		<h3 className="text-sm font-semibold text-(--text-primary)">{title}</h3>
		{children}
	</section>
);

// "HH:MM" string <-> Time, so the TimeField can bind to the stored cadence.
const parseAt = (at: string | null): Time | null => {
	if (!at) return null;
	const match = /^(\d{1,2}):(\d{2})$/.exec(at.trim());
	if (!match) return null;
	return new Time(Number(match[1]), Number(match[2]));
};

const formatAt = (time: Time | null): string | null => {
	if (!time) return null;
	return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(
		2,
		'0',
	)}`;
};

export const MonitorConfigBody = ({
	artifact,
	handlers,
	isReadOnly = false,
	showMetaRow = false,
	saveFailed = false,
}: MonitorConfigBodyProps) => {
	const content = artifact.content;
	const { onContentChange, onDirty, registerFlush, onRejectedTargetChange } =
		handlers;

	const sources = useMemo(() => content.sources ?? [], [content.sources]);
	const instruction = content.instruction ?? '';

	// Cadence is buffered locally and flushed on save/activate; every other field
	// persists live. Re-seed the buffer when the panel switches artifacts.
	const [cadence, setCadence] = useState<MonitorCadence>(
		content.cadence ?? DEFAULT_CADENCE,
	);
	const cadenceRef = useRef(cadence);
	cadenceRef.current = cadence;

	// Whether a PERSON moved a cadence control on this artifact. The buffer above
	// seeds from a default when `content.cadence` has not arrived yet, and the
	// flush below runs on unmount, so without this a panel that merely OPENED
	// wrote `daily 09:00` over whatever the customer had chosen and re-registered
	// their schedule to match (ADO-2047). The re-seed effect only depends on
	// `artifact.id`, so a late-arriving cadence never corrects the buffer either.
	// Writing only what someone actually changed closes both halves.
	//
	// One flag per FIELD, not one for the cadence. A gesture on the time must not
	// carry the buffered preset into the write, and the reverse. Any cadence
	// control added later has to set its own flag here, or its edit will not
	// persist.
	const cadenceTouchedRef = useRef({ preset: false, at: false });
	// `flush` below stays identity-stable, so it cannot close over `content`. It
	// reads the stored cadence through this ref instead, the way it reads sources.
	const contentCadenceRef = useRef(content.cadence);
	contentCadenceRef.current = content.cadence;

	// What the last flush already wrote, and what the store held when it wrote
	// it. The stored content stands still until the server echoes, so a second
	// flush before then read its own edit as a fresh change and sent it again.
	//
	// The record only stands while the store stands still. Once the store moves
	// it can move to something OTHER than what this flush wrote: a save that
	// failed keeps this body mounted with its record, and `commitSources` then
	// writes the array the store still holds, which puts the old value back. A
	// record kept past that point measured the edit against itself, reported no
	// change, and sent nothing — so the panel reported a save for a value it
	// never wrote. Expiring the record on a store move (inside useEchoBaseline)
	// puts the store back as the baseline (ADO-2047).
	const cadenceEcho = useEchoBaseline<MonitorCadence | null>(isEqual);
	const sourcesEcho = useEchoBaseline<MonitorSource[]>(isEqual);

	useEffect(() => {
		cadenceTouchedRef.current = { preset: false, at: false };
		sourcesEcho.reset();
		cadenceEcho.reset();
		setCadence(content.cadence ?? DEFAULT_CADENCE);
		// Only re-seed when the underlying artifact changes, not on every content
		// echo from our own live edits.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [artifact.id]);

	// A record assumes the write it describes actually reached the document. It
	// does not: `do_edit_artifact` can fail silently from this component's point
	// of view, and nothing here is told. Without this, a retry of the exact same
	// edit after a failed save reads as already-sent and nothing goes out a
	// second time — the "worst failure mode" this whole change exists to avoid
	// (ADO-2047). The panel's own save-failure state is the only signal telling
	// us a prior write may not have landed, so a retry has to measure against
	// the real store again.
	useEffect(() => {
		if (!saveFailed) return;
		cadenceEcho.reset();
		sourcesEcho.reset();
	}, [saveFailed, cadenceEcho, sourcesEcho]);

	// Track an upstream cadence the person has not overridden. The effect above
	// seeds once per artifact, so a cadence that changes while this panel is open
	// — an agent edit, or one that simply arrived late — left the controls showing
	// the old value, and the seed could hold the default over a real stored value.
	//
	// Re-seed per FIELD, never all-or-nothing: a touched field keeps the live
	// edit, and an untouched field follows content. A single guard on the whole
	// cadence would freeze both fields on one gesture, so the panel would show a
	// default time while the flush merged the stored time into the write. The
	// equality check keeps an echo from a source or instruction edit, which
	// carries the same cadence, from re-rendering in a loop.
	const upstreamPreset = content.cadence?.preset;
	const upstreamAt = content.cadence?.at;
	useEffect(() => {
		const touched = cadenceTouchedRef.current;
		const next = content.cadence ?? DEFAULT_CADENCE;
		setCadence((prev) => {
			const preset = touched.preset ? prev.preset : next.preset;
			// An untouched time follows content, but never into nothing. An hourly
			// monitor stores `at: null`, so an upstream switch to it blanked a fire
			// time the person could still see and was about to write.
			const at = touched.at ? prev.at : (next.at ?? prev.at);
			if (prev.preset === preset && prev.at === at) return prev;
			return { preset, at };
		});
		// Depend on the FIELDS, not the object: `content.cadence` is a fresh
		// reference on every content echo, and re-running on identity alone
		// re-entered this effect for edits that changed no cadence field.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [upstreamPreset, upstreamAt]);

	// Sources are normalised as part of the same flush: whatever gets saved has to be
	// a shape the sensing tool can consume, and blur proved unreliable through S2.
	const sourcesRef = useRef(sources);
	sourcesRef.current = sources;

	// Target text is buffered while it is being typed, exactly like cadence, and
	// written on flush. Committing per keystroke meant the document saw every
	// PREFIX, and a prefix can be individually valid on the way to something
	// invalid — typing the tag address stored "https://www.instagram.com/explor"
	// one character before `explore` was recognisable as a page (ADO-1890).
	const [draftTargets, setDraftTargets] = useState<Record<number, string>>({});
	const draftTargetsRef = useRef(draftTargets);
	draftTargetsRef.current = draftTargets;

	useEffect(() => {
		setDraftTargets({});
	}, [artifact.id]);

	// Held in a ref so `flush` below can stay identity-stable. The panel rebuilds
	// onContentChange whenever the bridge state object changes, which is on every
	// delta — with that in the dep array the effect re-ran mid-typing and its
	// CLEANUP flushed the half-typed buffer straight into the document, which is
	// the per-keystroke write this buffering exists to prevent, one level up.
	const onContentChangeRef = useRef(onContentChange);
	onContentChangeRef.current = onContentChange;

	useEffect(() => {
		const flush = () => {
			// Buffered target text lands here, normalised, and ONLY if the sensing
			// tool can use it. A target still carrying a problem is dropped rather
			// than written: it would be a document the backend refuses, and writing
			// it would also destroy the last value that worked.
			const storedSources = sourcesRef.current;
			const cleaned = storedSources.map((s, i) => {
				const typed = draftTargetsRef.current[i];
				if (typed === undefined) {
					return { ...s, target: checkMonitorTarget(s.tool, s.target).value };
				}
				const checked = checkMonitorTarget(s.tool, typed);
				if (checked.problem || monitorTrim(typed).length === 0) return { ...s };
				return { ...s, target: checked.value };
			});
			// Measure the diff against the last write only while the store still
			// holds what that write started from. Otherwise measure it against the
			// store, the way this did before the record existed.
			const baseline = sourcesEcho.resolve(storedSources);
			const changed = cleaned.some((s, i) => s.target !== baseline[i]?.target);
			// Send only what changed. An untouched cadence is not an edit, and
			// writing it is how an opened-and-closed panel used to overwrite the
			// customer's own choice. With nothing to send, send nothing: an empty
			// edit still reaches the artifact PATCH and still triggers a reschedule.
			//
			// Build the cadence from the STORED value plus only the fields someone
			// changed, and read the stored value NOW rather than at seed time — an
			// outside cadence edit that lands after the gesture must survive on a
			// field nobody touched.
			const touched = cadenceTouchedRef.current;
			const stored = contentCadenceRef.current ?? DEFAULT_CADENCE;
			const buffered = cadenceRef.current;
			const edits: Parameters<typeof onContentChange>[0] = {};
			let cadenceEdit: MonitorCadence | null = null;
			if (touched.preset || touched.at) {
				const overlaid = {
					...stored,
					...(touched.preset ? { preset: buffered.preset } : {}),
					...(touched.at ? { at: buffered.at } : {}),
				};
				// A preset that fires at a set time has to carry one. The stored
				// `at` is null on an hourly monitor, so a preset gesture merged that
				// null into the write and left a weekly monitor with no fire time.
				// Keep the time the person can see instead. Invent none when the
				// monitor never had one: writing a default nobody chose is the
				// defect this whole change exists to remove.
				const merged =
					cadenceUsesTime(overlaid.preset) && overlaid.at === null
						? { ...overlaid, at: buffered.at }
						: overlaid;
				// Not an edit when the artifact already holds this cadence, or when
				// the last flush wrote it and the store has not echoed yet. Save
				// calls this flush and then unmounts the body, whose cleanup calls
				// it again — without this the same edit landed a second time, after
				// do_save_artifact, which marked the panel dirty and took the saved
				// state off the screen.
				//
				// ONE baseline, exactly as the sources half picks one. While the
				// record stands, the record is what the document holds and the store
				// is one echo behind it, so the store is the wrong thing to compare
				// against: a person who went back to the cadence the store still
				// shows made a real edit, and reading the store called it a non-edit
				// and left the monitor on the schedule they abandoned. Once an
				// outside edit moves the store the record describes a store that is
				// gone, so the store is the baseline again.
				const storedNow = contentCadenceRef.current ?? null;
				const held = cadenceEcho.resolve(storedNow);
				if (!isEqual(held, merged)) {
					cadenceEdit = merged;
					edits.cadence = merged;
				}
			}
			if (changed) edits.sources = cleaned;
			if (Object.keys(edits).length === 0) return;
			// Record what this write carried AND what it started from, so the flush
			// that follows it can tell its own edit from a new one, and can tell
			// that the store has moved on. The buffered target TEXT is not cleared:
			// it is what the source box shows, and clearing it would put the stale
			// stored value back under the person until the server echoes. The
			// touched flags are not cleared either — a second gesture before the
			// echo still has to carry the field this write already moved.
			if (cadenceEdit)
				cadenceEcho.record(cadenceEdit, contentCadenceRef.current ?? null);
			if (changed) sourcesEcho.record(cleaned, storedSources);
			onContentChangeRef.current(edits);
		};
		registerFlush?.(flush);
		// Save/Activate call the registered flush explicitly, but Close and Back
		// hand off straight to the surrounding layout without going through it —
		// so a chosen-but-unbuffered cadence needs a flush of its own on every
		// other way this body can leave the screen, including a switch to a
		// different artifact. Unmount cleanup is the one hook that fires on all
		// of those paths alike, so the buffered cadence is written here rather
		// than duplicated at each panel's onClose/onBack. The flush carries only
		// what a gesture touched, so this path is silent when nothing changed.
		return flush;
	}, [registerFlush, cadenceEcho, sourcesEcho]);

	const commitSources = useCallback(
		(next: MonitorSource[]) => {
			onContentChange({ sources: next });
		},
		[onContentChange],
	);

	// Targets are buffered exactly like cadence: typing edits local state, and the
	// document is written on flush (Save, Activate, or leaving the panel). Per-
	// keystroke commits meant the artifact saw every PREFIX of what someone typed,
	// and a prefix can be individually valid on the way to something invalid —
	// typing the tag address stored "https://www.instagram.com/explor" one
	// character before `explore` was recognisable as a page. Nobody chose to save a
	// half-typed address, so nothing lands until the value settles.
	// Buffered, but still an edit. The cadence handlers buffer the same way and
	// call onDirty, and Save is gated on hasUnsavedEdits — so without this,
	// changing only a source target on an active monitor left Save disabled and
	// the edit was lost when the panel closed.
	const handleTargetChange = useCallback(
		(index: number, target: string) => {
			setDraftTargets((prev) => ({ ...prev, [index]: target }));
			onDirty?.();
		},
		[onDirty],
	);

	// What the box shows: the in-progress text when there is one, otherwise the
	// stored value. The note line and the Save/Activate gate both read this, so
	// the person sees the verdict on what is actually in front of them.
	const displayedTarget = useCallback(
		(index: number): string =>
			draftTargets[index] ?? sources[index]?.target ?? '',
		[draftTargets, sources],
	);
	const hasRejectedTarget = sources.some((s, i) => {
		const value = draftTargets[i] ?? s?.target ?? '';
		return (
			monitorTrim(value).length === 0 ||
			Boolean(checkMonitorTarget(s.tool, value).problem)
		);
	});

	useEffect(() => {
		onRejectedTargetChange?.(hasRejectedTarget);
	}, [hasRejectedTarget, onRejectedTargetChange]);

	// Normalise when the owner leaves the field, not on every keystroke — rewriting
	// text mid-type fights whoever is typing. Pasting a profile or subreddit URL is
	// the natural thing to do, and the sensing tools reject both: scrape_instagram
	// wants a bare handle, and reddit_top_posts passes its input straight through to
	// the actor. Left alone, the monitor runs forever and quietly finds nothing.
	const handleRemoveSource = useCallback(
		(index: number) => {
			// draftTargets is keyed by POSITION, and removing a source shifts every
			// later source down one. Without re-keying, the source that moves into
			// this slot inherits the buffered text of the source that just left,
			// which is text about a different site (ADO-1890).
			setDraftTargets((prev) => {
				const next: Record<number, string> = {};
				for (const [key, value] of Object.entries(prev)) {
					const at = Number(key);
					if (at === index) continue;
					next[at > index ? at - 1 : at] = value;
				}
				return next;
			});
			commitSources(sources.filter((_, i) => i !== index));
		},
		[sources, commitSources],
	);

	const handleAddSource = useCallback(() => {
		if (sources.length >= MAX_SOURCES) return;
		commitSources([...sources, { tool: 'scrape_website', target: '' }]);
	}, [sources, commitSources]);

	const handleToolChange = useCallback(
		(index: number, tool: MonitorSource['tool']) => {
			// Re-check the existing text against the NEW tool: the same string means
			// different things per tool, so switching to Instagram with a profile URL
			// in the box should resolve to the handle rather than stay a URL.
			// The text on SCREEN, not the committed value. Buffered text has not
			// been written yet, so reading s.target checked the previous string
			// against the new tool's rules and wrote the wrong target.
			const onScreen = draftTargetsRef.current[index] ?? sources[index]?.target;
			const rechecked = checkMonitorTarget(
				tool,
				onScreen ?? sources[index]?.target ?? '',
			);
			// The same refusal flush applies: a target the new tool cannot use is
			// NOT written. Switching a pasted tag address to Instagram used to
			// commit the rejected link, which is the one value the backend and
			// this PR's own e2e say can never reach the document, and it
			// overwrote a handle that worked.
			const keepsPreviousTarget = Boolean(rechecked.problem);
			const next = sources.map((s, i) =>
				i === index
					? {
							...s,
							tool,
							...(keepsPreviousTarget ? {} : { target: rechecked.value }),
						}
					: s,
			);
			setDraftTargets((prev) => {
				// A refused value stays on screen behind its red note, so the person
				// can correct it. Only a committed one is cleared.
				if (keepsPreviousTarget) {
					return onScreen === undefined ? prev : { ...prev, [index]: onScreen };
				}
				if (prev[index] === undefined) return prev;
				const copy = { ...prev };
				delete copy[index];
				return copy;
			});
			commitSources(next);
		},
		[sources, commitSources],
	);

	const handleInstructionChange = useCallback(
		(value: string) => {
			onContentChange({ instruction: value.slice(0, INSTRUCTION_LIMIT) });
		},
		[onContentChange],
	);

	// A control that moved without changing the value is not an edit. Marking the
	// buffer touched for one is how a stale cadence still reached the document:
	// the flag freezes that field against the upstream-tracking effect above for
	// the rest of the panel's life, so a later cadence — an agent edit, or one
	// that arrived late — never corrected the control, and the unmount flush then
	// wrote this buffer over that newer value. That is the exact harm the effect
	// exists to prevent.
	//
	// Both controls can report a non-edit. Clearing the time field emits null,
	// which `formatAt` cannot render, so the buffer keeps its old `at` and the
	// customer sees nothing change. S2's Picker fires its change even when the
	// key is unchanged, because `useSingleSelectListState` sets
	// `allowDuplicateSelectionEvents`.
	//
	// Only the fields this gesture actually MOVED are marked touched, so a preset
	// gesture cannot carry a stale buffered time into the flush.
	const commitCadence = useCallback(
		(change: Partial<MonitorCadence>) => {
			const prev = cadenceRef.current;
			const next = { ...prev, ...change };
			if (next.preset === prev.preset && next.at === prev.at) return;
			setCadence(next);
			if (next.preset !== prev.preset) cadenceTouchedRef.current.preset = true;
			if (next.at !== prev.at) cadenceTouchedRef.current.at = true;
			onDirty?.();
		},
		[onDirty],
	);

	const handlePresetChange = useCallback(
		(preset: MonitorCadence['preset']) => {
			commitCadence({ preset });
		},
		[commitCadence],
	);

	const handleTimeChange = useCallback(
		(time: Time | null) => {
			const at = formatAt(time);
			// A cleared field is not a choice of fire time, so it selects nothing.
			// Marking it touched would freeze the field against the stored value and
			// send a time nobody chose.
			if (at === null) return;
			commitCadence({ at });
		},
		[commitCadence],
	);

	const handleOutputModeChange = useCallback(
		(mode: 'chat_alert' | 'insight') => {
			onContentChange({ output_mode: mode });
		},
		[onContentChange],
	);

	const summary = monitorSummarySentence({
		// Preview what is on screen, including text still being typed — the
		// sentence is a live restatement of the form, not of the saved document.
		sources: sources.map((s, i) => ({
			...s,
			target: displayedTarget(i),
		})),
		instruction,
		cadence,
		output_mode: content.output_mode ?? 'chat_alert',
	});

	// Focus follows the collapse toggle. Both the Edit and Done buttons unmount
	// themselves when pressed, so without this focus falls to document.body and a
	// keyboard user has to walk the whole panel again to reach the field they just
	// opened. `moveFocus` is null on mount so nothing is stolen on first render.
	const instructionFieldRef = useRef<HTMLDivElement>(null);
	const editButtonRef = useRef<HTMLDivElement>(null);
	const moveFocus = useRef<'field' | 'edit' | null>(null);

	// Expanded when empty: an empty instruction disables Save, so a blank collapsed
	// row would hide the reason. Derived on mount and whenever the panel switches
	// artifacts — the same re-seed rule the cadence buffer uses.
	const [instructionExpanded, setInstructionExpanded] = useState(
		() => instruction.trim().length === 0,
	);
	useEffect(() => {
		setInstructionExpanded((content.instruction ?? '').trim().length === 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [artifact.id]);

	useEffect(() => {
		if (!moveFocus.current) return;
		const host =
			moveFocus.current === 'field'
				? instructionFieldRef.current
				: editButtonRef.current;
		moveFocus.current = null;
		host?.querySelector<HTMLElement>('textarea, button')?.focus();
	}, [instructionExpanded]);

	const lastRunAt = content.state?.last_run_at ?? null;

	return (
		<div className="flex flex-col gap-3 py-4">
			{/* What this monitor does, in one line, derived from the live fields below.
			    It sits where the authored description used to: that paragraph restated
			    these same values and went stale on the first edit, and the header title
			    truncates, so without this the panel would not say which monitor it is. */}
			{/* Clamped: the sentence embeds the instruction verbatim, and those run to
			    1000 chars — unclamped it would push the form off screen, which is the
			    problem the collapsed instruction below exists to solve. */}
			{/* The summary and the run state are one block: both describe the monitor as
			    it stands, and separating them by the same gap that separates the cards
			    would make the timestamp look like a card of its own. */}
			<div className="flex flex-col gap-1 pb-1">
				<div
					aria-live="polite"
					data-testid="monitor-summary"
					className="line-clamp-2 text-sm text-(--text-secondary)"
				>
					{summary}
				</div>
				{showMetaRow && (
					<div className="text-xs text-(--text-muted)">
						{lastRunAt
							? `Last checked ${new Date(lastRunAt).toLocaleString()}`
							: 'Not checked yet'}
					</div>
				)}
			</div>

			<SectionCard title="What to watch">
				{sources.length === 0 && (
					<p className="text-sm text-(--text-muted)">
						No sources yet — add one to start.
					</p>
				)}
				{sources.map((source, index) => (
					// items-end would bottom-align the 20px remove button against the 32px
					// fields, leaving it 6px low. The fields are the same height as each
					// other, so centring puts the X on their shared axis.
					// Sibling sources are delineated by the card's own hairline rather than
					// a per-row tint or radius — a row that looked like a sub-card inside
					// a card would compete with the card it belongs to. First row has no
					// rule: a line above the first item would read as a card divider.
					<div
						key={index}
						className={cn(
							'flex w-full flex-col gap-1',
							index > 0 && 'border-t border-black/[0.06] pt-3',
						)}
					>
						<div className="flex w-full items-center gap-2">
							<div className="w-40 shrink-0">
								<Picker
									aria-label="Source type"
									data-testid={`monitor-source-tool-${index}`}
									isDisabled={isReadOnly}
									selectedKey={source.tool}
									onSelectionChange={(key) =>
										handleToolChange(index, key as MonitorSource['tool'])
									}
								>
									{(
										Object.keys(SOURCE_TOOL_LABELS) as MonitorSource['tool'][]
									).map((tool) => (
										<PickerItem key={tool} id={tool}>
											{SOURCE_TOOL_LABELS[tool]}
										</PickerItem>
									))}
								</Picker>
							</div>
							<div className="flex-1">
								<TextField
									aria-label={`Target for source ${index + 1}`}
									data-testid={`monitor-source-target-${index}`}
									isDisabled={isReadOnly}
									value={displayedTarget(index)}
									onChange={(value) => handleTargetChange(index, value)}
									placeholder={SOURCE_TOOL_HINTS[source.tool].placeholder}
								/>
							</div>
							{!isReadOnly && (
								<ActionButton
									aria-label={`Remove ${source.target || `source ${index + 1}`}`}
									data-testid={`monitor-remove-source-${index}`}
									isQuiet
									size="XS"
									onPress={() => handleRemoveSource(index)}
								>
									<Close />
								</ActionButton>
							)}
						</div>
						{(() => {
							const checked = checkMonitorTarget(
								source.tool,
								displayedTarget(index),
							);
							// One line, always present. It swaps between the tool's help text
							// and whatever we made of the input — but it never unmounts, because
							// removing it as soon as someone types shifts the whole form up
							// under their cursor.
							const message =
								checked.problem ??
								checked.note ??
								SOURCE_TOOL_HINTS[source.tool].help;
							return (
								<p
									data-testid={
										(checked.problem ?? checked.note)
											? `monitor-source-note-${index}`
											: `monitor-source-help-${index}`
									}
									className={cn(
										'text-xs',
										checked.problem ? 'text-red-600' : 'text-(--text-muted)',
									)}
								>
									{message}
								</p>
							);
						})()}
					</div>
				))}
				{!isReadOnly && sources.length < MAX_SOURCES && (
					// Left-aligned to the card's content edge, and held to its own line so
					// it reads as an action on the list above rather than another row in it.
					<div className="-ml-2 self-start">
						<ActionButton
							isQuiet
							size="S"
							data-testid="monitor-add-source"
							onPress={handleAddSource}
						>
							<Add />
							{/* S2 resolves children by slot: an icon beside a bare string has no
							    text slot, so it stacks them onto two lines. */}
							<Text>Add source</Text>
						</ActionButton>
					</div>
				)}
			</SectionCard>

			{/* Not "How often": that is the S2 label on the preset Picker one line below,
			    and a card titled the same as its own first field says it twice. The
			    visible field labels are load-bearing (they are what balances this row),
			    so the card title moves instead. */}
			<SectionCard title="How often it checks">
				{/* No width forcing: S2 sizes both controls intrinsically, which is what
				    keeps them proportionate. Wrapping at a narrow panel width is the
				    intended behavior — better than crushing both onto one line. */}
				<div className="flex flex-wrap items-end gap-3">
					<Picker
						label="How often"
						data-testid="monitor-cadence-preset"
						isDisabled={isReadOnly}
						selectedKey={cadence.preset}
						onSelectionChange={(key) =>
							handlePresetChange(key as MonitorCadence['preset'])
						}
					>
						{CADENCE_PRESETS.map((preset) => (
							<PickerItem key={preset.value} id={preset.value}>
								{preset.label}
							</PickerItem>
						))}
					</Picker>
					{cadenceUsesTime(cadence.preset) && (
						<TimeField
							label="Time of day"
							data-testid="monitor-cadence-time"
							isDisabled={isReadOnly}
							value={parseAt(cadence.at)}
							onChange={handleTimeChange}
						/>
					)}
				</div>
				{cadence.preset === 'twice_daily' &&
					twiceDailyTimesLabel(cadence.at) && (
						<p className="text-xs text-(--text-muted)">
							Checks at {twiceDailyTimesLabel(cadence.at)}.
						</p>
					)}
			</SectionCard>

			<SectionCard title="What to look for">
				{/* These instructions are written by the agent and run long — left
				    unbounded the field grew tall enough to push the destination, the
				    summary and Save off screen. Collapsed to one line by default so the
				    whole form stays visible; expanded when there is nothing yet, because
				    an empty instruction blocks Save and a blank collapsed row would give
				    no clue why. */}
				{instructionExpanded ? (
					<div ref={instructionFieldRef}>
						<TextArea
							aria-label="What to look for"
							data-testid="monitor-instruction"
							isDisabled={isReadOnly}
							value={instruction}
							onChange={handleInstructionChange}
							placeholder="e.g. Alert me if any product price changes"
						/>
					</div>
				) : (
					<div ref={editButtonRef} className="flex items-center gap-2">
						<p
							data-testid="monitor-instruction-preview"
							className="min-w-0 flex-1 truncate text-sm
								text-(--text-secondary)"
						>
							{instruction}
						</p>
						<ActionButton
							isQuiet
							size="S"
							data-testid="monitor-instruction-edit"
							onPress={() => {
								moveFocus.current = 'field';
								setInstructionExpanded(true);
							}}
						>
							<Text>{isReadOnly ? 'View' : 'Edit'}</Text>
						</ActionButton>
					</div>
				)}
				{instructionExpanded && instruction.trim().length > 0 && (
					<div className="-ml-2 self-start">
						<ActionButton
							isQuiet
							size="S"
							data-testid="monitor-instruction-done"
							onPress={() => {
								moveFocus.current = 'edit';
								setInstructionExpanded(false);
							}}
						>
							<Text>Done</Text>
						</ActionButton>
					</div>
				)}
				{/* Counter lives outside the collapse so it is never hidden with the field. */}
				{instruction.length > COUNTER_THRESHOLD && (
					<p className="self-end text-xs text-(--text-muted)">
						{instruction.length} / {INSTRUCTION_LIMIT}
					</p>
				)}
			</SectionCard>

			<SectionCard title="When it finds something">
				<Picker
					aria-label="When it finds something"
					data-testid="monitor-output-mode"
					isDisabled={isReadOnly}
					selectedKey={content.output_mode ?? 'chat_alert'}
					onSelectionChange={(key) =>
						handleOutputModeChange(key as 'chat_alert' | 'insight')
					}
				>
					<PickerItem id="chat_alert">Message me in chat</PickerItem>
					<PickerItem id="insight">Add to my insights</PickerItem>
				</Picker>
				<p className="text-xs text-(--text-muted)">
					{(content.output_mode ?? 'chat_alert') === 'insight'
						? "I'll collect what I find in your insights feed."
						: "I'll message you in chat the moment I find something."}
				</p>
			</SectionCard>
		</div>
	);
};
